import _ from 'lodash'
import {MutationTree} from 'vuex'
import {AuthorizationState} from '@/modules/authorization/store/index'
import Permission from '@/modules/authorization/model/Permission'
import Role from '@/modules/authorization/model/Role'
import Pagination from '@/modules/core/model/pagination'
import RoleUserAssignation from '@/modules/authorization/model/RoleUserAssignation'

export enum MutationTypes {
    SetPermissionTree = 'SET_PERMISSION_TREE',
    SetCurrentPermission = 'SET_CURRENT_PERMISSION',
    AddPermission = 'ADD_PERMISSION',
    ToggleEditingPermission = 'TOGGLE_EDITING_PERMISSION',
    DeletePermission = 'DELETE_PERMISSION',
    ResetPermission = 'RESET_PERMISSION',
    UpdatePermissionField = 'UPDATE_PERMISSION_FIELD',
    // Mutaciones para roles
    SetRoleTree = 'SET_ROLE_TREE',
    SetCurrentRole = 'SET_CURRENT_ROLE',
    AddRole = 'ADD_ROLE',
    UpdateRoleField = 'UPDATE_ROLE_FIELD',
    AssignPermissionsToRole = 'ASSIGN_PERMISSIONS_TO_ROLE',
    ToggleEditingRole = 'TOGGLE_EDITING_ROLE',
    ResetRole = 'RESET_ROLE',
    DeleteRole = 'DELETE_ROLE',

    SetRoleUserAssignationList = 'SET_ROLE_USER_ASSIGNATION_LIST',
    SetUpdatedRoleUserAssignationList = 'SET_UPDATED_ROLE_USER_ASSIGNATION_LIST',
    SetGrantedPermission = 'SET_GRANTED_PERMISSIONS',
    SetRejectedPermissions = 'SET_REJECTED_PERMISSIONS',
    MergeGrantedPermissions = 'MERGE_GRANTED_PERMISSIONS',
    MergeRejectedPermissions = 'MERGE_REJECTED_PERMISSIONS',
}

const mutation: MutationTree<AuthorizationState> = {
    /**
     * Establece el permiso argumentado como permiso raíz y por tanto comienzo del árbol
     *
     * @param state
     * @param tree
     */
    [MutationTypes.SetPermissionTree]: (state, tree: Permission) => state.permissions = tree,

    /**
     * Establece el permiso actualmente seleccionado por el usuario
     *
     * @param state
     * @param tree
     */
    [MutationTypes.SetCurrentPermission]: (state, path: string) => {
        if (!path) {
            return
        }

        const permission = state.permissions.findByPath(path)
        if (!permission) {
            throw new Error(`Se ha intentado establecer como permiso en edición un permiso que no existe en el árbol`)
        }

        state.permission = _.cloneDeep(permission) as Permission
    },

    /**
     * Añade un permiso a uno de los nodos del árbol
     */
    [MutationTypes.AddPermission]: (state, payload: {path: string, permission: Permission}) => {
        const { path, permission } = payload
        if (!path) {
            return
        }

        const parent = state.permissions.findByPath(path)
        if (!parent) {
            throw new Error(`Se esta intentando insertar un nodo en un permiso que no existe, path: ${path}`)
        }

        parent.addChild(permission)
    },

    /**
     * Establece si el usuario se encuentra creando un permiso en la aplicación
     *
     * @param state
     * @param creating
     */
    [MutationTypes.ToggleEditingPermission]: (state, editing: boolean) => state.editingPermission = editing,

    /**
     * Elimina un permiso del árbol
     *
     * @param state
     * @param path
     */
    [MutationTypes.DeletePermission]: (state, path: string) => state.permissions.deletePermission(path),

    /**
     * Restablece el permiso actualmente en edición
     *
     * @param state
     */
    [MutationTypes.ResetPermission]: (state) => state.permission = new Permission(),

    /**
     * Actualiza un campo del permiso actualmente en edición
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdatePermissionField]: (state, payload: {field: string, value: any}) => {
        _.set(state.permission, payload.field, payload.value)
    },

    /**
     * Establece el árbol del permisos cargados en la aplicación
     *
     * @param state
     * @param tree
     */
    [MutationTypes.SetRoleTree]: (state, tree: Role) => state.roles = tree,

    /**
     * Establece el rol que se encuentra actualmente en edición por el usuario
     *
     * @param state
     * @param path
     */
    [MutationTypes.SetCurrentRole]: (state, path: string) => {
        if (!path) {
            return
        }

        const role = state.roles.findByPath(path)
        if (!role) {
            throw new Error(`Se ha intentado establecer en edición un rol que no existe en el árbol`)
        }

        state.role = _.cloneDeep(role) as Role
    },

    /**
     * Limpia el rol actualmente en edición
     *
     * @param state
     */
    [MutationTypes.ResetRole]: (state) => state.role = new Role(),

    /**
     * Actualiza un campo del rol actualmente en edición
     */
    [MutationTypes.UpdateRoleField]: (state, payload: {field: string, value: any}) => {
        _.set(state.role, payload.field, payload.value)
    },

    /**
     * Modifica el rol actualmente en edición por el usuario y registra en este
     * los permisos asociados a el
     *
     * @param state
     * @param paths
     */
    [MutationTypes.AssignPermissionsToRole]: (state, paths: string[]) => state.role.permissions = paths,

    /**
     * Establece si el usuario esta editando el rol actual
     *
     * @param state
     * @param editing
     */
    [MutationTypes.ToggleEditingRole]: (state, editing: boolean) => state.editingRole = editing,

    /**
     * Añade un permiso a uno de los nodos del árbol
     */
    [MutationTypes.AddRole]: (state, payload: {path: string, role: Role}) => {
        const { path, role } = payload
        if (!path) {
            return
        }

        const parent = state.roles.findByPath(path)
        if (!parent) {
            throw new Error(`Se esta intentando insertar un rol cuyo padre "${path}" no existe`)
        }

        parent.addChild(role)
    },
    /**
     * Establece la lista de usuario asignados a un rol mostrada actualmente al usuario
     *
     * @param state
     * @param list
     */
    [MutationTypes.SetRoleUserAssignationList]: (state, list: Pagination<RoleUserAssignation>) => {
        state.roleUserAssignationList = list
    },

    /**
     * Establece la lista de asignaciones que será enviada al servidor
     *
     * @param state
     * @param list
     */
    [MutationTypes.SetUpdatedRoleUserAssignationList]: (state, list: RoleUserAssignation[]) => {
        state.updatedRoleUserAssignationList = list
    },
    /**
     * Establece la lista de permisos permitidos para el usuario
     *
     * @param state
     * @param granted
     */
    [MutationTypes.SetGrantedPermission]: (state, granted: string[]) => {
        state.grantedPermissions = granted
    },
    /**
     * Establece la lista de permisos rechazados para el usuario
     * @param state
     * @param rejected
     */
    [MutationTypes.SetRejectedPermissions]: (state, rejected: Array<{path: string, rejectionMessage: string}>) => {
        state.rejectedPermissions = rejected
    },

    /**
     * Elimina un rol de usuario
     *
     * @param state
     * @param path
     */
    [MutationTypes.DeleteRole]: (state, path: string) => {
        state.roles.removeChild(path)
    },

    [MutationTypes.MergeGrantedPermissions]: (state, granted: string[]) => {
        state.grantedPermissions = state.grantedPermissions.concat(granted)
    },

    [MutationTypes.MergeRejectedPermissions]: (state, rejected: Array<{path: string, rejectionMessage: string}>) => {
        state.rejectedPermissions = state.rejectedPermissions.concat(rejected)
    },
}

export default mutation

